import { skipToken } from "@reduxjs/toolkit/query";
import type { ConfigurationTestResultId } from "@repo/app-types/entities/configurationTestResult";
import type {
  CopilotProposalId,
  CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import {
  PlaybookBranch,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { RESEARCH_AGENT_ID } from "@repo/app-types/entities/researchAgent";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  allStaticAgentInstructionTileKeys,
  resolveStaticAgentInstructionTileKey,
  STATIC_AGENT_INSTRUCTION_TILES,
  TeammateAgentBranch,
} from "@repo/app-types/entities/teammateAgent";
import {
  agentInstructionSectionCandidateDraftSchema,
  type AgentInstructionSectionCandidateDraft,
} from "@repo/app-types/entities/voiceAgentConfigurationTools";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton, iconButton } from "@repo/ui/Buttons/iconButton";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import { ChatEmptyState } from "@repo/ui/chat/chatEmptyState";
import { ChatHeader, chatHeaderIconClassName } from "@repo/ui/chat/chatHeader";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
} from "@repo/ui/chat/chatMessage";
import { ChatThread } from "@repo/ui/chat/chatThread";
import { ChatThreadSkeleton } from "@repo/ui/chat/chatThreadSkeleton";
import { messageItemStyle } from "@repo/ui/chat/messageItem";
import { MessageThinkingIndicator } from "@repo/ui/chat/messageThinkingIndicator";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { GradingReferenceProvider } from "@repo/ui/structuredContent/gradingReferenceContext";
import { ModelOutputContent } from "@repo/ui/structuredContent/modelOutputContent";
import * as Sentry from "@sentry/react";
import {
  IconAlertTriangle,
  IconClock,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import type { UIMessage } from "ai";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type FC,
  type ReactNode,
} from "react";
import { ToggleButton } from "react-aria-components";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useListThreadProposalsQuery } from "../../api/entities/copilotProposal.js";
import { useMastraThreadQuery } from "../../api/entities/mastraThread.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { appPaths } from "../../app/appPaths.js";
import { buildPlaybookProposalEditLocation } from "../../components/playbookEditorUrlState.js";
import { useAppSelector } from "../../hooks/hook.js";
import { buildInteractionResolverFromMessages } from "../agents/interactionResolver.js";
import { useMastraChatContext } from "../agents/mastra/mastraChatContext.js";
import { checkIsTestCaseProposalData } from "../agents/teammateAgentProposalCardContent.js";
import { TeammateAgentTestCaseProposalCard } from "../agents/teammateAgentTestCaseProposalCard.js";
import { ComparePlaybookNodeProposalTool } from "../agents/tools/comparePlaybookNodeProposalTool.js";
import { CountCustomerInteractionsTool } from "../agents/tools/countCustomerInteractionsTool.js";
import { FallbackTool } from "../agents/tools/fallbackTool.js";
import { GetCoachingNotesTool } from "../agents/tools/getCoachingNotesTool.js";
import { GetCriterionDetailsTool } from "../agents/tools/getCriterionDetailsTool.js";
import { GetKbAdherenceFindingTool } from "../agents/tools/getKbAdherenceFindingTool.js";
import { GetKnowledgeDocumentTool } from "../agents/tools/getKnowledgeDocumentTool.js";
import { GetSectionDetailsTool } from "../agents/tools/getSectionDetailsTool.js";
import { GetSignalDetailsTool } from "../agents/tools/getSignalDetailsTool.js";
import { ListCustomerInteractionsTool } from "../agents/tools/listCustomerInteractionsTool.js";
import { ProposeKnowledgeDocumentEditTool } from "../agents/tools/proposeKnowledgeDocumentEditTool.js";
import { ProposePlaybookNodeTool } from "../agents/tools/proposePlaybookNodeTool.js";
import { ProposePlaybookOutcomeTool } from "../agents/tools/proposePlaybookOutcomeTool.js";
import { ResearchCustomerInteractionsTool } from "../agents/tools/researchCustomerInteractionsTool.js";
import { ResearchTeammatesTool } from "../agents/tools/researchTeammatesTool.js";
import { SearchTeammatesTool } from "../agents/tools/searchTeammatesTool.js";
import { SimulateRolePlayRealismTool } from "../agents/tools/simulateRolePlayRealismTool.js";
import { SimulateVoiceAgentConversationTool } from "../agents/tools/simulateVoiceAgentConversationTool.js";
import { StartAutomationPreviewRunTool } from "../agents/tools/startAutomationPreviewRunTool.js";
import {
  TestPlaybookNodeTool,
  TestPlaybookOutcomeTool,
} from "../agents/tools/testPlaybookTool.js";
import {
  ProposeVoiceAgentInstructionsTool,
  TestVoiceAgentInstructionsTool,
} from "../agents/tools/voiceAgentInstructionsTools.js";
import { GradingReferenceLinkAdapter } from "../teammatePerformance/gradingReferenceLinkAdapter.js";
import {
  CopilotDeepLinkList,
  type CopilotDeepLinkListRow,
} from "./copilotDeepLinkList.js";
import {
  buildCopilotProposalLocation,
  buildTurnTestResultLocation,
} from "./copilotDeepLinks.js";
import { CopilotProposalCard } from "./copilotProposalCard.js";
import {
  countTurnProposals,
  selectTurnProposals,
  selectTurnTestResults,
  type PlaybookTurnTestResult,
  type TeammateAgentTurnTestResult,
  type TurnTestResult,
} from "./copilotProposalTurns.js";
import { useCopilotSuggestions } from "./copilotSuggestions.js";
import { selectEditorProposalStatus } from "./editorProposalsSlice.js";
import { ResearchAgentChatMenu } from "./researchAgentChatMenu.js";
import {
  FALLBACK_THREAD_TITLE,
  researchAgentThreadMetadataSchema,
  type ResearchAgentProps,
} from "./researchAgentContext.js";

type ResearchAgentChatSurface = "panel" | "modal";

export interface ResearchAgentChatViewProps {
  researchAgent: ResearchAgentProps;
  /**
   * `panel` renders the compact side-panel chat; `modal` renders the expanded
   * chat column with its content centered on a wide rail.
   */
  surface: ResearchAgentChatSurface;
  onRequestClose: () => void;
  /** Panel-only history content below the persistent toolbar. */
  history?: ReactNode;
  isHistoryOpen?: boolean;
  onHistoryOpenChange?: (isOpen: boolean) => void;
  /** Panel-only: open the expanded modal. */
  onRequestExpand?: () => void;
}

const modalContentRailClassName = "mx-auto w-full max-w-[760px]";
const modalEmptyStateRailClassName = "mx-auto w-full max-w-[680px]";

/**
 * The research agent conversation: header, message thread, and composer. The
 * same view drives the docked side panel and the expanded modal.
 */
export const ResearchAgentChatView: FC<ResearchAgentChatViewProps> = (
  props,
) => {
  const {
    resourceId,
    threadId,
    threadTitle,
    threadResourceId,
    threadMetadata,
    isThreadNotFound,
  } = props.researchAgent;

  const isModalSurface = props.surface === "modal";
  const isHistoryOpen = props.isHistoryOpen ?? false;
  const isOwnThread = threadResourceId === resourceId;

  // Get the Mastra thread corresponding to the selected thread ID.
  // `currentData` rather than `data`: the latter holds on to the previously
  // selected thread until the new one's fetch settles, which would flash that
  // thread's title in the header and apply its share state to this one.
  const { currentData: currentThread } = useMastraThreadQuery(
    threadId !== undefined
      ? { agentId: RESEARCH_AGENT_ID, threadId }
      : skipToken,
  );

  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);

  const chat = useMastraChatContext();
  const lastMessageId = chat.messages.at(-1)?.id;
  const isEmpty = chat.messages.length === 0 && !chat.isLoading;

  // The modal's regular empty state renders the composer inline; every other
  // state (including "chat not found", which has no inline composer) keeps
  // the pinned one.
  const showsPinnedComposer = !(
    isModalSurface &&
    isOwnThread &&
    isEmpty &&
    !isThreadNotFound
  );

  const focusComposer = useEffectEvent(() => {
    const composerInput = composerInputRef.current;
    composerInput?.focus({ preventScroll: true });
    if (document.activeElement === composerInput) {
      return undefined;
    }

    // The focus didn't take, which means an ancestor is still `inert`: the
    // panel clears that when its own ref attaches, and a parent's refs attach
    // after its children's layout effects run. Retry once the commit is done.
    const frame = requestAnimationFrame(() =>
      composerInput?.focus({ preventScroll: true }),
    );
    return () => cancelAnimationFrame(frame);
  });

  // Starting a new chat is the only thing that moves focus here by itself.
  // Comparing against the request the view was built with (rather than a
  // "have I mounted yet" flag) keeps StrictMode's double-invoked effects from
  // counting as a request, so opening a page never steals the user's
  // keystrokes.
  const { composerFocusRequestId } = props.researchAgent;
  const handledFocusRequestIdRef = useRef(composerFocusRequestId);
  useLayoutEffect(() => {
    if (handledFocusRequestIdRef.current === composerFocusRequestId) {
      return undefined;
    }

    handledFocusRequestIdRef.current = composerFocusRequestId;
    return isOwnThread ? focusComposer() : undefined;
  }, [composerFocusRequestId, isOwnThread, focusComposer]);

  // Sending the first message moves the modal's composer out of the empty
  // state and into the footer, destroying the focused textarea. Left alone,
  // the dialog's focus scope hands focus to the header's New chat button,
  // which paints a focus ring there. This only recovers focus the swap
  // orphaned — if it went anywhere else, it stays there.
  const previousShowsPinnedComposerRef = useRef(showsPinnedComposer);
  useLayoutEffect(() => {
    const didComposerMove =
      previousShowsPinnedComposerRef.current !== showsPinnedComposer;
    previousShowsPinnedComposerRef.current = showsPinnedComposer;

    if (!didComposerMove || document.activeElement !== document.body) {
      return undefined;
    }

    return focusComposer();
  }, [showsPinnedComposer, focusComposer]);

  // Falling back through the title the thread was selected with keeps the
  // header on the right name while its own fetch is in flight. An unsent
  // thread and one still awaiting its generated title share the last fallback,
  // so the header holds steady until the real title lands.
  const headerTitle =
    currentThread?.title || threadTitle || FALLBACK_THREAD_TITLE;

  // The freshly fetched thread has the latest share state; validate it rather
  // than trusting the loosely-typed Mastra metadata.
  const currentThreadMetadataParse =
    researchAgentThreadMetadataSchema.safeParse(currentThread?.metadata);
  const currentThreadMetadata = currentThreadMetadataParse.success
    ? currentThreadMetadataParse.data
    : undefined;

  // Each turn that proposed something lists what it proposed, right there in
  // the transcript. Rows carry live status, so a proposal you later applied or
  // discarded reads as decided without leaving the turn it came from.
  const navigate = useNavigate();
  const { data: threadProposals, refetch: refetchThreadProposals } =
    useListThreadProposalsQuery(
      threadId !== undefined ? { threadId } : skipToken,
    );

  // The agent writes proposals server-side mid-turn, which no client mutation
  // observes — so refetch whenever the transcript has more of them than the
  // last fetch accounted for.
  const turnProposalCount = countTurnProposals(chat.messages);
  useEffect(() => {
    if (turnProposalCount > 0) {
      void refetchThreadProposals();
    }
  }, [turnProposalCount, refetchThreadProposals]);

  const proposalsById = useMemo(
    () => new Map((threadProposals ?? []).map((entry) => [entry.id, entry])),
    [threadProposals],
  );

  // A deep-link row opens its proposal in its editor; the other resource
  // types' cards navigate or apply themselves.
  const openProposalInEditor = (proposalId: CopilotProposalId) => {
    const proposal = proposalsById.get(proposalId);
    switch (proposal?.resource_type) {
      case "playbook":
        navigate(
          buildPlaybookProposalEditLocation({
            playbookId: proposal.resource.id,
            proposalId: proposal.id,
          }),
        );
        break;
      case "teammateAgent":
        navigate(
          buildCopilotProposalLocation(
            appPaths.teammateAgentInstructions.build({
              teammateAgentId: proposal.resource.id,
            }),
            proposal.id,
          ),
        );
        break;
      case "knowledgeBaseAttachment":
        navigate(
          buildCopilotProposalLocation(
            appPaths.settingsKnowledgeBaseAttachment.build({
              knowledgeBaseId: proposal.data.knowledge_base_id,
              attachmentId: proposal.resource.id,
            }),
            proposal.id,
          ),
        );
        break;
    }
  };

  const suggestions = useCopilotSuggestions();
  const handleSuggestionSelect = (prompt: string) => {
    props.researchAgent.onNewThreadSelect({ initialUserMessageText: prompt });
  };

  const footerClassName = twMerge(
    "shrink-0 bg-card",
    isModalSurface ? "px-6 pt-3 pb-6" : "px-3 pt-2 pb-3",
  );

  const composer = (
    <ChatComposer
      variant="boxed"
      inputRef={composerInputRef}
      placeholder="Ask a question..."
      isRunning={chat.isRunning}
      onStop={chat.stop}
      onSend={chat.sendMessage}
      // Controlled from the provider: the panel and the modal never render at
      // once, so an internally-held draft would be lost moving between them.
      value={props.researchAgent.composerDraft}
      onValueChange={props.researchAgent.onComposerDraftChange}
    />
  );

  const emptyState = isThreadNotFound ? (
    <ThreadNotFoundState />
  ) : isModalSurface && isOwnThread ? (
    // In the modal the empty state centers vertically, with the composer
    // inline underneath the suggestions instead of pinned to the bottom.
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-5">
      <div className={twMerge("flex flex-col", modalEmptyStateRailClassName)}>
        <ChatEmptyState
          suggestions={suggestions}
          onSuggestionSelect={handleSuggestionSelect}
        />
        <div className="mt-8">{composer}</div>
      </div>
    </div>
  ) : (
    <div className="flex flex-1 flex-col items-center px-3 pt-[clamp(5rem,18vh,9rem)] pb-5">
      <ChatEmptyState
        className="w-[calc(100%-1.5rem)] max-w-[420px]"
        suggestions={isOwnThread ? suggestions : []}
        onSuggestionSelect={handleSuggestionSelect}
      />
    </div>
  );

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden bg-card">
      <ChatHeader
        className={
          isModalSurface
            ? "border-b border-border-light bg-card px-6 py-4"
            : "bg-card px-3 pt-3 pb-2"
        }
        title={isHistoryOpen ? "Chat history" : headerTitle}
        titleAccessory={
          !isHistoryOpen && !isOwnThread ? (
            <div className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
              View only
            </div>
          ) : null
        }
        actions={
          <>
            <IconButton
              accessibleLabel="New chat"
              className="hover:bg-muted"
              variant={ButtonVariant.Tertiary}
              icon={<IconPlus className={chatHeaderIconClassName} />}
              onClick={() => {
                props.onHistoryOpenChange?.(false);
                if (threadId === undefined && isEmpty && !chat.isRunning) {
                  focusComposer();
                } else {
                  props.researchAgent.onNewThreadSelect();
                }
              }}
            />

            {props.onHistoryOpenChange ? (
              <ToggleButton
                aria-label="Chat history"
                isSelected={isHistoryOpen}
                onChange={props.onHistoryOpenChange}
                className={iconButton({
                  variant: ButtonVariant.Tertiary,
                  className: "hover:bg-muted selected:bg-muted",
                })}
              >
                <IconClock className={chatHeaderIconClassName} />
              </ToggleButton>
            ) : null}

            <ResearchAgentChatMenu
              researchAgent={props.researchAgent}
              threadMetadata={currentThreadMetadata ?? threadMetadata}
              onRequestExpand={props.onRequestExpand}
            />

            <IconButton
              accessibleLabel="Close research assistant"
              className="hover:bg-muted"
              variant={ButtonVariant.Tertiary}
              icon={<IconX className={chatHeaderIconClassName} />}
              onClick={props.onRequestClose}
            />
          </>
        }
      />
      <div className="relative flex min-h-0 flex-1 flex-col overflow-clip">
        <div
          ref={(element) => {
            if (element) element.inert = isHistoryOpen;
          }}
          className={twMerge(
            "flex min-h-0 flex-1 flex-col transition-opacity duration-150 motion-reduce:transition-none",
            isHistoryOpen && "pointer-events-none opacity-0",
          )}
        >
          <ChatThread
            className="min-h-0 bg-card"
            messages={chat.messages}
            isLoading={chat.isLoading}
            loadingIndicator={
              <ChatThreadSkeleton
                className={
                  isModalSurface ? modalContentRailClassName : undefined
                }
              />
            }
            showsEdgeFades
            timelineClassName={isModalSurface ? "px-6 py-5" : "px-3 py-2"}
            emptyState={emptyState}
            // Turns break wider than the parts within a turn. Without the inner
            // gap, an assistant turn's text and tool-call rows stack flush: the
            // bubble backgrounds that used to separate them are gone on this
            // surface, so spacing has to do that work.
            messagesClassName="gap-4"
            renderMessage={(message) => (
              <div
                className={twMerge(
                  // `space-y` rather than a flex gap: flex items default to
                  // `min-width: auto`, which stops long markdown (code blocks,
                  // tables, URLs) from shrinking and overflows the panel.
                  "max-w-full min-w-0 space-y-3 break-words [&_a]:break-all [&_pre]:max-w-full [&_pre]:whitespace-pre-wrap",
                  isModalSurface && modalContentRailClassName,
                )}
              >
                <Sentry.ErrorBoundary fallback={<AssistantMessageError />}>
                  <ChatMessage
                    message={message}
                    isStreaming={chat.isRunning && message.id === lastMessageId}
                    tools={TOOL_COMPONENTS}
                    renderText={renderResearchText}
                    renderTypingIndicator={() => <MessageThinkingIndicator />}
                  />
                </Sentry.ErrorBoundary>
                <TurnProposalList
                  message={message}
                  proposalsById={proposalsById}
                  onSelect={openProposalInEditor}
                />
                <TurnTestResultList
                  message={message}
                  proposalsById={proposalsById}
                  onSelect={(testResult) => {
                    navigate(buildTurnTestResultLocation(testResult));
                  }}
                />
              </div>
            )}
            composer={
              !isOwnThread ? (
                <div
                  className={twMerge(
                    footerClassName,
                    "text-center text-sm text-muted-foreground",
                  )}
                >
                  This chat is view-only. You cannot send messages.
                </div>
              ) : showsPinnedComposer ? (
                <div className={footerClassName}>
                  <div
                    className={
                      isModalSurface ? modalContentRailClassName : undefined
                    }
                  >
                    {composer}
                  </div>
                </div>
              ) : undefined
            }
          />
        </div>
        {props.history ? (
          <div
            ref={(element) => {
              if (element) element.inert = !isHistoryOpen;
            }}
            className={twMerge(
              "absolute inset-0 flex min-h-0 flex-col bg-card transition-opacity duration-150 motion-reduce:transition-none",
              !isHistoryOpen && "pointer-events-none opacity-0",
            )}
          >
            {props.history}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * The proposals one turn put up for review. Rendered under that turn's
 * message, so the transcript reads as a history of what was proposed and what
 * became of it, rather than one running list of everything outstanding. A
 * proposal either has a card of its own or a row in the deep-link list: a
 * voice agent's test case applies from its card, while its instruction
 * proposals and knowledge base document edits open in their editors.
 */
const TurnProposalList: FC<{
  message: UIMessage;
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>;
  onSelect: (proposalId: CopilotProposalId) => void;
}> = ({ message, proposalsById, onSelect }) => {
  const proposals = useMemo(
    () => selectTurnProposals(message, proposalsById),
    [message, proposalsById],
  );
  // The row the open editor is showing. The editor reports at most one, so
  // the first match is the only one.
  const activeRowId = useAppSelector((state) =>
    proposals.find(
      (proposal) =>
        selectEditorProposalStatus(state, proposal.id) === "viewing",
    ),
  )?.id;
  if (proposals.length === 0) {
    return null;
  }

  // A row names its playbook or voice agent only when the turn's rows span
  // more than one.
  const playbookIds = new Set(
    proposals.flatMap((proposal) =>
      proposal.resource_type === "playbook" ? [proposal.resource.id] : [],
    ),
  );
  const teammateAgentIds = new Set(
    proposals.flatMap((proposal) =>
      proposal.resource_type === "teammateAgent" ? [proposal.resource.id] : [],
    ),
  );
  const rows: CopilotDeepLinkListRow<CopilotProposalId>[] = [];
  const cards: ReactNode[] = [];
  for (const proposal of proposals) {
    switch (proposal.resource_type) {
      case "signal":
      case "automation":
      case "training":
        cards.push(
          <CopilotProposalCard key={proposal.id} proposal={proposal} />,
        );
        break;
      case "playbook":
        rows.push(buildProposalRow(proposal, playbookIds.size > 1));
        break;
      case "teammateAgent":
        if (checkIsTestCaseProposalData(proposal.data)) {
          cards.push(
            <TeammateAgentTestCaseProposalCard
              key={proposal.id}
              proposal={proposal}
              data={proposal.data}
            />,
          );
        } else {
          rows.push(buildProposalRow(proposal, teammateAgentIds.size > 1));
        }
        break;
      case "knowledgeBaseAttachment":
        cards.push(
          <CopilotProposalCard key={proposal.id} proposal={proposal} />,
        );
        break;
    }
  }
  return (
    <div className="space-y-3">
      {rows.length > 0 ? (
        <CopilotDeepLinkList
          rows={rows}
          activeRowId={activeRowId}
          onSelect={(row) => onSelect(row.id)}
        />
      ) : null}
      {cards}
    </div>
  );
};

/** A proposal's deep-link row, naming its resource where the list spans several. */
function buildProposalRow(
  proposal: CopilotProposalSummary,
  showsResourceName: boolean,
): CopilotDeepLinkListRow<CopilotProposalId> {
  return {
    id: proposal.id,
    resourceType: proposal.resource_type,
    name: proposal.node_name,
    ...(showsResourceName ? { resourceName: proposal.resource.name } : {}),
    status: proposal.status,
  };
}

/**
 * The test results one turn saved, rendered like its proposals. A test result
 * has no lifecycle, but it measured one revision: it opens while that is
 * still the resource's current revision and reads as outdated once the
 * resource changes. One list per playbook or voice agent, so each can check
 * its revision.
 */
const TurnTestResultList: FC<{
  message: UIMessage;
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>;
  onSelect: (testResult: TurnTestResult) => void;
}> = ({ message, proposalsById, onSelect }) => {
  // One list per resource, so each can check its revision.
  const testResultsByResource = useMemo(
    () => groupTurnTestResultsByResource(message, proposalsById),
    [message, proposalsById],
  );
  const showsResourceName = testResultsByResource.size > 1;

  return (
    <>
      {[...testResultsByResource.values()].map((group) => {
        switch (group.resource_type) {
          case "playbook":
            return (
              <PlaybookTestResultList
                key={group.key}
                playbookId={group.resourceId}
                testResults={group.testResults}
                showsPlaybookName={showsResourceName}
                onSelect={onSelect}
              />
            );
          case "teammateAgent":
            return (
              <TeammateAgentTestResultList
                key={group.key}
                teammateAgentId={group.resourceId}
                testResults={group.testResults}
                showsAgentName={showsResourceName}
                onSelect={onSelect}
              />
            );
        }
      })}
    </>
  );
};

type TurnTestResultGroup =
  | {
      key: string;
      resource_type: "playbook";
      resourceId: PlaybookId;
      testResults: PlaybookTurnTestResult[];
    }
  | {
      key: string;
      resource_type: "teammateAgent";
      resourceId: TeammateId;
      testResults: TeammateAgentTurnTestResult[];
    };

/** A turn's test results by the resource they measured, in the order they ran. */
function groupTurnTestResultsByResource(
  message: UIMessage,
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>,
): ReadonlyMap<string, TurnTestResultGroup> {
  const groups = new Map<string, TurnTestResultGroup>();
  for (const testResult of selectTurnTestResults(message, proposalsById)) {
    switch (testResult.resource_type) {
      case "playbook": {
        const key = `${testResult.resource_type}:${testResult.playbook.id}`;
        const group = groups.get(key);
        if (group?.resource_type === "playbook") {
          group.testResults.push(testResult);
        } else {
          groups.set(key, {
            key,
            resource_type: "playbook",
            resourceId: testResult.playbook.id,
            testResults: [testResult],
          });
        }
        break;
      }
      case "teammateAgent": {
        const key = `${testResult.resource_type}:${testResult.teammate_agent_id}`;
        const group = groups.get(key);
        if (group?.resource_type === "teammateAgent") {
          group.testResults.push(testResult);
        } else {
          groups.set(key, {
            key,
            resource_type: "teammateAgent",
            resourceId: testResult.teammate_agent_id,
            testResults: [testResult],
          });
        }
        break;
      }
    }
  }
  return groups;
}

/** One resource's test result rows, marking the one the open editor shows. */
const TestResultList = <T extends TurnTestResult>({
  testResults,
  buildRow,
  onSelect,
}: {
  testResults: readonly T[];
  buildRow: (
    testResult: T,
  ) => CopilotDeepLinkListRow<ConfigurationTestResultId>;
  onSelect: (testResult: T) => void;
}) => {
  const rows = testResults.map(buildRow);
  const activeRowId = useAppSelector((state) =>
    rows.find((row) => selectEditorProposalStatus(state, row.id) === "viewing"),
  )?.id;
  return (
    <CopilotDeepLinkList
      title="Test results"
      activeRowId={activeRowId}
      rows={rows}
      onSelect={(row) => {
        const testResult = testResults.find(
          (candidate) => candidate.test_result_id === row.id,
        );
        if (testResult) {
          onSelect(testResult);
        }
      }}
    />
  );
};

/**
 * A playbook's test results measured one revision: the draft branch resolves
 * to the revision the editor works on (the draft, falling back to live).
 * Until it loads, rows open; the editor's fetch omits an outdated result
 * anyway.
 */
const PlaybookTestResultList: FC<{
  playbookId: PlaybookId;
  testResults: readonly PlaybookTurnTestResult[];
  /** Set when the turn's test results span more than one resource. */
  showsPlaybookName: boolean;
  onSelect: (testResult: PlaybookTurnTestResult) => void;
}> = ({ playbookId, testResults, showsPlaybookName, onSelect }) => {
  const { data: playbook } = usePlaybookByIdQuery({
    id: playbookId,
    branch: PlaybookBranch.Draft,
  });
  return (
    <TestResultList
      testResults={testResults}
      buildRow={(testResult) => ({
        id: testResult.test_result_id,
        resourceType: "playbook" as const,
        name: testResult.node_name,
        ...(showsPlaybookName
          ? { resourceName: testResult.playbook.name }
          : {}),
        ...(playbook && playbook.revision_id !== testResult.playbook_revision_id
          ? { status: "stale" as const }
          : {}),
      })}
      onSelect={onSelect}
    />
  );
};

/**
 * A voice agent's test results measured the revision the checks read: the
 * draft, falling back to live. Rows are named by the tiles the tested
 * candidate changed, or as a check of the saved instructions.
 */
const TeammateAgentTestResultList: FC<{
  teammateAgentId: TeammateId;
  testResults: readonly TeammateAgentTurnTestResult[];
  /** Set when the turn's test results span more than one resource. */
  showsAgentName: boolean;
  onSelect: (testResult: TeammateAgentTurnTestResult) => void;
}> = ({ teammateAgentId, testResults, showsAgentName, onSelect }) => {
  const { data: teammateAgent } = useGetTeammateAgentQuery({
    id: teammateAgentId,
    branch: TeammateAgentBranch.Draft,
  });
  const currentRevisionId =
    teammateAgent?.teammate_agent_config_revision_id ??
    teammateAgent?.teammate_agent_config.live_revision_id;
  return (
    <TestResultList
      testResults={testResults}
      buildRow={(testResult) => ({
        id: testResult.test_result_id,
        resourceType: "teammateAgent" as const,
        name: describeTestedInstructions(testResult.candidate_draft),
        ...(showsAgentName && teammateAgent
          ? { resourceName: teammateAgent.name }
          : {}),
        ...(teammateAgent &&
        currentRevisionId !== testResult.teammate_agent_config_revision_id
          ? { status: "stale" as const }
          : {}),
      })}
      onSelect={onSelect}
    />
  );
};

/** The tiles a tested candidate changed, in tile order, or the saved instructions. */
function describeTestedInstructions(
  candidateDraft: AgentInstructionSectionCandidateDraft | null,
): string {
  const changedSections = candidateDraft
    ? agentInstructionSectionCandidateDraftSchema
        .keyof()
        .options.filter((section) => candidateDraft[section] !== undefined)
    : [];
  const tileKeys = new Set(
    changedSections.map(resolveStaticAgentInstructionTileKey),
  );
  const labels = allStaticAgentInstructionTileKeys
    .filter((tileKey) => tileKeys.has(tileKey))
    .map((tileKey) => STATIC_AGENT_INSTRUCTION_TILES[tileKey].label);
  return labels.length > 0 ? labels.join(", ") : "Instruction checks";
}

const ThreadNotFoundState: FC = () => (
  <div className="flex flex-1 flex-col items-center justify-center p-5">
    <div className="flex flex-col items-center gap-4">
      <IconAlertTriangle className="size-12 text-gray-400" />
      <div className="text-center">
        <h2 className="mb-1 text-base font-semibold text-foreground">
          Chat not found
        </h2>
        <p className="text-sm text-muted-foreground">
          This chat doesn&apos;t exist or you don&apos;t have access to it.
        </p>
      </div>
    </div>
  </div>
);

const TOOL_COMPONENTS = {
  byName: {
    searchTeammatesTool: SearchTeammatesTool,
    researchTeammatesTool: ResearchTeammatesTool,
    countCustomerInteractionsTool: CountCustomerInteractionsTool,
    listCustomerInteractionsTool: ListCustomerInteractionsTool,
    researchCustomerInteractionsTool: ResearchCustomerInteractionsTool,
    getCriterionDetailsTool: GetCriterionDetailsTool,
    getSectionDetailsTool: GetSectionDetailsTool,
    getKnowledgeDocumentTool: GetKnowledgeDocumentTool,
    getKbAdherenceFindingTool: GetKbAdherenceFindingTool,
    getSignalDetailsTool: GetSignalDetailsTool,
    proposeKnowledgeDocumentEditTool: ProposeKnowledgeDocumentEditTool,
    getCoachingNotesTool: GetCoachingNotesTool,
    // Pre-rename key, kept so historical threads render.
    retrieveCoachingNotesTool: GetCoachingNotesTool,
    comparePlaybookNodeProposalTool: ComparePlaybookNodeProposalTool,
    // Pre-rename key, kept so historical threads render.
    testCriterionProposalTool: ComparePlaybookNodeProposalTool,
    proposePlaybookNodeTool: ProposePlaybookNodeTool,
    proposePlaybookOutcomeTool: ProposePlaybookOutcomeTool,
    testPlaybookNodeTool: TestPlaybookNodeTool,
    testPlaybookOutcomeTool: TestPlaybookOutcomeTool,
    testVoiceAgentInstructionsTool: TestVoiceAgentInstructionsTool,
    proposeVoiceAgentInstructionsTool: ProposeVoiceAgentInstructionsTool,
    simulateVoiceAgentConversationTool: SimulateVoiceAgentConversationTool,
    simulateRolePlayRealismTool: SimulateRolePlayRealismTool,
    startAutomationPreviewRunTool: StartAutomationPreviewRunTool,
  },
  Fallback: FallbackTool,
};

// The research agent uses the `agent` layout it shares with the configuration
// assistants: a muted user bubble above naked assistant text, both
// left-aligned and shrinking to fit. (Simulations keep `simulation`, whose
// right-aligned `max-w-[80%]` bubbles suit a transcript of a two-party
// conversation.)
// Both roles render structured content, which styles its own elements, so
// neither bubble opts into the `prose` variant.
const userStyle = messageItemStyle({ role: "user", layout: "agent" });
const assistantStyle = messageItemStyle({
  role: "assistant",
  layout: "agent",
});

/** Renders a research-agent text part using the `agent` layout. */
function renderResearchText({
  text,
  role,
  isStreaming,
}: ChatMessageRenderTextArgs) {
  // User prompts go through the same structured content pipeline as the
  // assistant's text, for consistency; user input is about as trusted as model
  // output.
  const style = role === "user" ? userStyle : assistantStyle;
  return (
    <div className={style.row()}>
      <div className={style.bubble()}>
        <ResearchMessageTextContent text={text} isStreaming={isStreaming} />
      </div>
    </div>
  );
}

const ResearchMessageTextContent: FC<{
  text: string;
  isStreaming: boolean;
}> = ({ text, isStreaming }) => {
  const chat = useMastraChatContext();

  // Quotes and references cite interactions returned by earlier tool calls in
  // the thread, so resolve them against those tool results.
  const resolveInteractionId = useMemo(
    () => buildInteractionResolverFromMessages(chat.messages),
    [chat.messages],
  );

  return (
    <GradingReferenceProvider value={GradingReferenceLinkAdapter}>
      <ModelOutputContent
        text={text}
        isStreaming={isStreaming}
        resolveInteractionId={resolveInteractionId}
      />
    </GradingReferenceProvider>
  );
};

const AssistantMessageError: FC = () => (
  <div
    className={messageItemStyle({
      role: "assistant",
      layout: "agent",
      isError: true,
    }).bubble({ className: "px-3.5" })}
  >
    Unable to render this message.
  </div>
);
